import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable } from 'rxjs';
import { Attachment, UploadParameters, UploadParametersRequest } from '../app/models/attachment';
import { environment } from '../environments/environment';

@Injectable({
  providedIn: 'root'
})
export class AttachmentService {
  private readonly apiUrl = `${environment.api.baseUrl}${environment.api.endpoints.attachments}`;

  constructor(private http: HttpClient) {}

  /**
   * Get upload parameters for a file
   */
  getUploadParameters(request: UploadParametersRequest): Observable<UploadParameters> {
    // Build query parameters for GET request
    const params = new URLSearchParams();
    params.set('fileName', request.fileName);
    params.set('contentType', request.contentType);
    params.set('size', request.size.toString());
    params.set('holderName', 'PARTNER');

    return this.http.get<UploadParameters>(`${this.apiUrl}/upload-parameters?${params.toString()}`);
  }

  /**
   * Upload file to the provided URL (S3 compatible)
   */
  uploadFile(file: File, uploadParams: UploadParameters): Observable<any> {
    // For S3 uploads, we need to send the file directly as the request body
    // with proper headers, not as FormData
    const headers: { [key: string]: string } = {
      'Content-Type': file.type
    };

    // Add any additional headers from upload parameters
    if (uploadParams.fields) {
      Object.keys(uploadParams.fields).forEach(key => {
        headers[key] = uploadParams.fields![key];
      });
    }

    return this.http.put(uploadParams.url, file, { 
      headers: new HttpHeaders(headers),
      reportProgress: true
    });
  }

  /**
   * Get attachment by ID
   */
  getAttachment(id: number): Observable<Attachment> {
    return this.http.get<Attachment>(`${this.apiUrl}/${id}`);
  }

  /**
   * Delete attachment
   */
  deleteAttachment(id: number): Observable<void> {
    return this.http.delete<void>(`${this.apiUrl}/${id}`);
  }


  /**
   * Upload file with progress tracking
   */
  uploadFileWithProgress(file: File): Observable<{ progress: number; attachmentId?: number }> {
    console.log('🔥 AttachmentService.uploadFileWithProgress called with file:', file.name);
    const request: UploadParametersRequest = {
      fileName: file.name,
      contentType: file.type,
      size: file.size
    };

    return new Observable(observer => {
      // Emit initial progress
      console.log('🔥 Emitting initial progress: 0');
      observer.next({ progress: 0 });
      
      console.log('🔥 Getting upload parameters...');
      this.getUploadParameters(request).subscribe({
        next: (uploadParams) => {
          console.log('🔥 Got upload parameters:', uploadParams);
          // Emit progress for getting parameters
          console.log('🔥 Emitting progress: 25');
          observer.next({ progress: 25 });
          
          console.log('🔥 Starting file upload to S3...');
          this.uploadFile(file, uploadParams).subscribe({
            next: (response) => {
              console.log('🔥 S3 upload completed, response:', response);
              // Emit progress for upload completion
              console.log('🔥 Emitting progress: 75');
              observer.next({ progress: 75 });
              
              // Small delay to show progress
              console.log('🔥 Setting timeout for final completion...');
              setTimeout(() => {
                console.log('🔥 Emitting final progress: 100 with attachmentId:', uploadParams.id);
                observer.next({ progress: 100, attachmentId: uploadParams.id });
                console.log('🔥 Completing observable');
                observer.complete();
              }, 100);
            },
            error: (error) => observer.error(error)
          });
        },
        error: (error) => observer.error(error)
      });
    });
  }
}
